import { useQuery } from "@tanstack/react-query";

const AUTH_URL = import.meta.env.VITE_AUTH_URL;

export interface User {
  name: string;
  email: string;
  user_type: string;
  is_privileged: boolean;
}

export interface Permission {
  key: string;
  name: string;
  scope: string;
}

export interface Feature {
  key: string;
  name: string;
  icon: string;
  route: string;
  order: number;
  permissions: Permission[];
}

export interface Department {
  key: string;
  name: string;
  description: string;
  department_url: string;
  icon: string;
  order: number;
  features: Feature[];
}

export interface AccessMatrix {
  user: User;
  departments: Department[];
}

interface UseAuthOptions {
  // Set to false to skip the /me/access network call (e.g. when a local token is present)
  enabled?: boolean;
}

export function useAuth({ enabled = true }: UseAuthOptions = {}) {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["auth-me"],
    queryFn: async (): Promise<AccessMatrix> => {
      // credentials: "include" is automatically added by the global fetch interceptor
      const response = await fetch(`${AUTH_URL}/me/access`);

      if (!response.ok) {
        throw new Error("Unauthorized");
      }
      const resData = await response.json();
      return resData.data;
    },
    enabled,          // Controlled by caller — prevents unnecessary network calls
    retry: false,     // Do not retry on 401s
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });

  const hasPermission = (
    deptKey: string,
    featureKey: string,
    permissionKey: string
  ): boolean => {
    if (!data) return false;

    // Privileged users always have access
    if (
      data.user.is_privileged ||
      ["root", "system_admin"].includes(data.user.user_type)
    ) {
      return true;
    }

    const dept = data.departments.find((d) => d.key === deptKey);
    if (!dept) return false;

    const feature = dept.features.find((f) => f.key === featureKey);
    if (!feature) return false;

    return feature.permissions.some((p) => p.key === permissionKey);
  };

  return {
    user: data?.user,
    departments: data?.departments,
    isLoading,
    isError,
    error,
    hasPermission,
    isAuthenticated: !!data?.user,
  };
}
